import React, { useEffect, useState, useCallback, useRef } from "react";
import { Download, Share, X, Plus, Smartphone, MoreVertical } from "lucide-react";
import { useApp } from "@/context";

/**
 * PWA Install system — exposes:
 *   <PWAInstallPrompt />   → bottom-sheet banner (auto + manual-triggered)
 *   <PWAInstallButton />   → header/nav pill button, always visible unless installed
 *
 * Uses a module-level singleton event bus so the button can open the banner
 * without prop-drilling, and the banner can listen for the deferred
 * `beforeinstallprompt` event exactly once across the whole app.
 */

const DISMISS_KEY = "rg_pwa_dismissed_at";
const DISMISS_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000; // 3 days (shorter than before)

// ---------- singleton state ----------
let _deferred = null;
const _listeners = new Set();
function _emit() { _listeners.forEach((fn) => { try { fn(); } catch {} }); }

if (typeof window !== "undefined" && !window.__rg_pwa_wired) {
  window.__rg_pwa_wired = true;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _deferred = e;
    _emit();
  });
  window.addEventListener("appinstalled", () => {
    _deferred = null;
    try { localStorage.removeItem(DISMISS_KEY); } catch {}
    _emit();
  });
}

// ---------- helpers ----------
export function isRunningStandalone() {
  if (typeof window === "undefined") return false;
  if (window.navigator.standalone === true) return true;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  return false;
}
export function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua)
    || (ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document);
}
export function isIOSSafari() {
  if (!isIOSDevice()) return false;
  const ua = navigator.userAgent || "";
  return !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
}
export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

// ---------- Hook: triggers re-render when deferred prompt / install state changes ----------
function usePwaState() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((x) => x + 1);
    _listeners.add(fn);
    return () => { _listeners.delete(fn); };
  }, []);
  return {
    tick,
    canPrompt: !!_deferred,
    installed: isRunningStandalone(),
  };
}

// ---------- Banner component ----------
export default function PWAInstallPrompt() {
  const { lang } = useApp();
  const { canPrompt, installed } = usePwaState();
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState("chromium"); // chromium | ios | other
  const openedOnce = useRef(false);

  const openBanner = useCallback(() => {
    if (installed) return;
    if (isIOSSafari()) setMode("ios");
    else if (canPrompt) setMode("chromium");
    else setMode("other"); // fallback instructions for any unknown browser
    setVisible(true);
  }, [canPrompt, installed]);

  // Auto-open once per session, respecting cooldown — mobile customers only
  useEffect(() => {
    if (installed || openedOnce.current) return undefined;
    if (!(isIOSDevice() || isAndroid())) return undefined;
    if (/^\/(admin|ops|staff|purchases|buy|products-entry)/.test(window.location.pathname)) return undefined;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && (Date.now() - dismissedAt < DISMISS_COOLDOWN_MS)) return undefined;
    const timer = setTimeout(() => {
      if (!openedOnce.current) {
        openedOnce.current = true;
        openBanner();
      }
    }, 3500);
    return () => clearTimeout(timer);
  }, [canPrompt, installed, openBanner]);

  // Listen for manual open requests from the header button
  useEffect(() => {
    const onOpen = () => openBanner();
    window.addEventListener("rg:pwa-open", onOpen);
    return () => window.removeEventListener("rg:pwa-open", onOpen);
  }, [openBanner]);

  const hide = (persist = true) => {
    setVisible(false);
    if (persist) {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    }
  };

  const install = async () => {
    if (!_deferred) { setMode("other"); return; }
    try {
      _deferred.prompt();
      const { outcome } = await _deferred.userChoice;
      _deferred = null;
      _emit();
      hide(outcome !== "accepted");
    } catch { hide(true); }
  };

  if (!visible || installed) return null;
  const ar = lang === "ar";
  const showChromium = mode === "chromium" && canPrompt;
  const showIOS = mode === "ios";
  const showOther = !showChromium && !showIOS;

  return (
    <div
      className="fixed bottom-[88px] sm:bottom-6 left-3 right-3 sm:left-auto sm:right-6 sm:max-w-sm z-[60]"
      role="dialog" aria-live="polite"
      data-testid="pwa-install-banner"
    >
      <div className="rounded-2xl shadow-2xl overflow-hidden"
           style={{ background: "linear-gradient(135deg,#000000 0%,#1F1F1F 100%)" }}>
        <div className="p-4 text-white">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 shadow-lg"
                 style={{ background: "linear-gradient(135deg,#000000 0%,#dbbf95 100%)", color: "#000000" }}>
              <Smartphone size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold leading-tight">
                {ar ? "ثبّت تطبيق RARE GROUP" : "Install RARE GROUP"}
              </div>
              <div className="text-[13px] opacity-85 mt-0.5 leading-relaxed">
                {showChromium
                  ? (ar
                      ? "ثبّته على شاشتك الرئيسية — بنقرة واحدة."
                      : "Add it to your home screen in one tap.")
                  : showIOS
                    ? (ar
                        ? "أضفه لشاشتك لتفتحه بدون متصفح ولا رابط."
                        : "Add to your Home Screen — no browser, no URL.")
                    : (ar
                        ? "افتح القائمة ⋮ في متصفحك ثم اختر «تثبيت التطبيق»."
                        : "Open your browser's ⋮ menu and tap 'Install app'.")}
              </div>
            </div>
            <button onClick={() => hide(true)}
                    className="shrink-0 opacity-75 hover:opacity-100 -mt-1 -me-1 p-1"
                    aria-label={ar ? "إغلاق" : "Close"}
                    data-testid="pwa-install-close">
              <X size={16} />
            </button>
          </div>

          {showIOS && (
            <div className="mt-3 pt-3 border-t border-white/15 text-[13px] leading-relaxed">
              <ol className="space-y-1.5 list-none p-0">
                <li className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/15 text-[11px] font-bold shrink-0">1</span>
                  <span className="flex items-center gap-1.5 flex-wrap">
                    {ar ? "اضغط" : "Tap"}
                    <Share size={14} style={{ color: "#dbbf95" }} />
                    {ar ? "في الأسفل" : "at the bottom"}
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/15 text-[11px] font-bold shrink-0">2</span>
                  <span className="flex items-center gap-1.5 flex-wrap">
                    {ar ? "اختر" : "Choose"}
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/15 text-[11px]">
                      <Plus size={10} />
                      {ar ? "إضافة للشاشة" : "Add to Home"}
                    </span>
                  </span>
                </li>
              </ol>
              <button onClick={() => hide(true)}
                      className="w-full mt-3 py-2 rounded-xl text-sm font-medium"
                      style={{ background: "#dbbf95", color: "#000000" }}
                      data-testid="pwa-ios-dismiss">
                {ar ? "فهمت" : "Got it"}
              </button>
            </div>
          )}

          {showOther && (
            <div className="mt-3 pt-3 border-t border-white/15 text-[13px] leading-relaxed space-y-1.5">
              <div className="flex items-center gap-1.5">
                <MoreVertical size={14} style={{ color: "#dbbf95" }} />
                <span>
                  {ar
                    ? "افتح قائمة ⋮ (ثلاث نقاط) بأعلى المتصفح"
                    : "Open your browser's ⋮ menu (top right)"}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Download size={14} style={{ color: "#dbbf95" }} />
                <span>
                  {ar
                    ? "اختر «تثبيت التطبيق» أو «إضافة إلى الشاشة الرئيسية»"
                    : "Tap 'Install app' or 'Add to Home screen'"}
                </span>
              </div>
              <button onClick={() => hide(true)}
                      className="w-full mt-2 py-2 rounded-xl text-sm font-medium"
                      style={{ background: "#dbbf95", color: "#000000" }}
                      data-testid="pwa-other-dismiss">
                {ar ? "فهمت" : "Got it"}
              </button>
            </div>
          )}

          {showChromium && (
            <div className="flex items-center gap-2 mt-3">
              <button onClick={install}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                      style={{ background: "#dbbf95", color: "#000000" }}
                      data-testid="pwa-install-btn">
                <Download size={15} />
                {ar ? "ثبّت الآن" : "Install now"}
              </button>
              <button onClick={() => hide(true)}
                      className="py-2.5 px-3 rounded-xl text-sm opacity-80 hover:opacity-100"
                      data-testid="pwa-install-later">
                {ar ? "لاحقاً" : "Later"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Header pill button — always visible when NOT already installed.
 * Clicking it dispatches a custom event the banner listens for.
 */
export function PWAInstallButton({ className = "" }) {
  const { lang } = useApp();
  const { installed } = usePwaState();
  if (installed) return null;
  const ar = lang === "ar";
  return (
    <button
      onClick={() => window.dispatchEvent(new CustomEvent("rg:pwa-open"))}
      className={`chip flex items-center gap-1 ${className}`}
      data-testid="pwa-install-header-btn"
      style={{
        background: "linear-gradient(135deg,#000000 0%,#dbbf95 100%)",
        color: "#000000",
        borderColor: "transparent",
        fontWeight: 600,
      }}
      title={ar ? "ثبّت التطبيق" : "Install app"}
    >
      <Download size={14} />
      <span className="hidden sm:inline">{ar ? "ثبّت التطبيق" : "Install app"}</span>
      <span className="sm:hidden">{ar ? "ثبّت" : "Install"}</span>
    </button>
  );
}
